import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { TAROT_DECK } from '@/data/tarotDeck';

interface TarotDeckLibraryProps {
  onClose: () => void;
  onSelectCard: (card: any) => void;
}

export default function TarotDeckLibrary({ onClose, onSelectCard }: TarotDeckLibraryProps) {
  const [cards, setCards] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSuit, setSelectedSuit] = useState<string>('all');

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    const { data } = await supabase
      .from('tarot_cards')
      .select('card_name, card_number, suit, image_url, upright_meaning, reversed_meaning, keywords, story, imagery_explanation')
      .eq('is_published', true)
      .order('sort_order');

    if (data && data.length > 0) {
      // Map database fields to component expected fields
      const mappedCards = data.map(card => ({
        ...card,
        name: card.card_name,
        number: card.card_number,
        imageUrl: card.image_url,
        uprightMeaning: card.upright_meaning,
        reversedMeaning: card.reversed_meaning
      }));
      setCards(mappedCards);
    } else {
      console.warn('⚠️ No cards found in database, using fallback');
      // Use fallback data
      setCards(TAROT_DECK as any[]);
    }
  };

  const suits = ['all', 'major', 'Wands', 'Cups', 'Swords', 'Pentacles'];

  const filteredCards = cards.filter(card => {
    const cardName = card.card_name || card.name;
    const cardSuit = card.suit || '';
    const matchesSuit = selectedSuit === 'all' || 
                       cardSuit.toLowerCase() === selectedSuit.toLowerCase() ||
                       (selectedSuit === 'major' && cardSuit.toLowerCase() === 'major arcana') ||
                       (selectedSuit === 'Major Arcana' && cardSuit.toLowerCase() === 'major');
    const matchesSearch = cardName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         card.keywords?.some((k: string) => k.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSuit && matchesSearch;
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Tarot Deck Library</DialogTitle>
          <DialogDescription>Browse the complete tarot deck</DialogDescription>
        </DialogHeader>
        <div className="p-6 space-y-4">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              Complete Tarot Deck
            </DialogTitle>
          </DialogHeader>

          {/* Search and Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search cards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              {suits.map(suit => (
                <Badge
                  key={suit}
                  variant={selectedSuit === suit ? 'default' : 'outline'}
                  className="cursor-pointer hover:bg-primary/20"
                  onClick={() => setSelectedSuit(suit)}
                >
                  {suit}
                </Badge>
              ))}
            </div>
          </div>

          {/* Cards Grid */}
          <ScrollArea className="h-[60vh]">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-1">
              {filteredCards.map((card, idx) => (
                <div
                  key={idx}
                  onClick={() => onSelectCard(card)}
                  className="cursor-pointer group"
                >
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gradient-to-br from-primary/5 to-accent/5 relative shadow-lg transition-all group-hover:shadow-2xl group-hover:scale-105 border-2 border-primary/20 flex items-center justify-center">
                     {(card.image_url || card.imageUrl) ? (
                      <>
                     <img 
                          src={card.image_url || card.imageUrl} 
                          alt={card.card_name || card.name}
                          className="w-full h-full object-contain p-3"
                          loading="lazy"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling;
                            if (fallback) (fallback as HTMLElement).style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full hidden flex-col items-center justify-center p-4 text-center bg-gradient-to-br from-primary/10 to-accent/10 absolute inset-0">
                          <div className="text-4xl font-bold text-primary mb-2">
                            {card.card_number || card.number}
                          </div>
                          <div className="text-sm font-semibold text-foreground">
                            {card.card_name || card.name}
                          </div>
                          <div className="text-xs text-muted-foreground mt-2">
                            Image unavailable
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
                        <div className="text-4xl font-bold text-primary mb-2">
                          {card.card_number || card.number}
                        </div>
                        <div className="text-sm font-semibold text-foreground">
                          {card.card_name || card.name}
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                      <span className="text-foreground text-sm font-semibold">View Card</span>
                    </div>
                  </div>
                  <div className="mt-2 text-center">
                    <p className="text-xs font-medium truncate">{card.card_name || card.name}</p>
                    <p className="text-xs text-muted-foreground">{card.suit}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
